import React, { useMemo, useCallback } from 'react';
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
  MarkerType,
  type Node,
  type Edge,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import dagre from 'dagre';
import PSIFlowStageNode, { type StageNodeData } from './PSIFlowStageNode';
import type { PSIFlowDetail, FlowStage } from '@/types/psiFlow';
import { useTranslation } from 'react-i18next';

const NODE_WIDTH = 240;
const NODE_HEIGHT = 160;

const nodeTypes = { stageNode: PSIFlowStageNode };

interface ProcurementFlowViewProps {
  flowDetail: PSIFlowDetail;
  onOpenDocument?: (type: string, id: string) => void;
  onNodeClick?: (stage: FlowStage, flowDetail: PSIFlowDetail) => void;
}

const getLayoutedElements = (nodes: Node[], edges: Edge[]) => {
  const dagreGraph = new dagre.graphlib.Graph();
  dagreGraph.setDefaultEdgeLabel(() => ({}));
  dagreGraph.setGraph({ rankdir: 'LR', nodesep: 100, ranksep: 120 });

  nodes.forEach((node) => {
    dagreGraph.setNode(node.id, { width: NODE_WIDTH, height: NODE_HEIGHT });
  });
  edges.forEach((edge) => {
    dagreGraph.setEdge(edge.source, edge.target);
  });

  dagre.layout(dagreGraph);

  const layoutedNodes = nodes.map((node) => {
    const nodeWithPosition = dagreGraph.node(node.id);
    return {
      ...node,
      position: {
        x: nodeWithPosition.x - NODE_WIDTH / 2,
        y: nodeWithPosition.y - NODE_HEIGHT / 2,
      },
    };
  });

  return { nodes: layoutedNodes, edges };
};

const ProcurementFlowView: React.FC<ProcurementFlowViewProps> = ({
  flowDetail,
  onOpenDocument,
  onNodeClick,
}) => {
  const { t } = useTranslation('psi');

  const po = flowDetail.purchase_order;
  const inbounds = flowDetail.inbounds || [];
  const invoices = flowDetail.invoices || [];

  const hasInbound = inbounds.length > 0;
  const latestInbound = hasInbound ? inbounds[0] : null;
  const inboundCompleted = inbounds.some((ib) => ib.status === 'completed');
  const hasInvoice = invoices.length > 0;
  const latestInvoice = hasInvoice ? invoices[0] : null;
  const invoicePaid = invoices.some((inv) => inv.status === 'paid');

  const isOverduePO =
    po &&
    po.status === 'ordered' &&
    new Date(po.expected_delivery) < new Date() &&
    !inboundCompleted;

  const { nodes: initialNodes, edges: initialEdges } = useMemo(() => {
    const nodes: Node<StageNodeData>[] = [
      {
        id: 'po',
        type: 'stageNode',
        position: { x: 0, y: 0 },
        draggable: false,
        data: {
          stage: 'purchase_order',
          label: t('flow.stages.purchase_order', 'Purchase Order'),
          docNumber: po?.po_number,
          status: po?.status,
          amount: po?.grand_total,
          currency: po?.currency,
          date: po?.purchase_date ? new Date(po.purchase_date).toLocaleDateString() : undefined,
          isOverdue: isOverduePO,
          hasDocument: !!po,
          isCompleted: po?.status === 'closed' || po?.status === 'received',
          onOpen: po ? () => onOpenDocument?.('po', po.po_id) : undefined,
          onClick: po ? () => onNodeClick?.('purchase_order', flowDetail) : undefined,
        },
      },
      {
        id: 'inbound',
        type: 'stageNode',
        position: { x: 0, y: 0 },
        draggable: false,
        data: {
          stage: 'inbound',
          label: t('flow.stages.inbound', 'Inbound/IQC'),
          docNumber: latestInbound?.inbound_number,
          status: latestInbound?.status,
          amount: latestInbound?.total_amount,
          currency: latestInbound?.currency,
          date: latestInbound?.received_date
            ? new Date(latestInbound.received_date).toLocaleDateString()
            : undefined,
          hasDocument: hasInbound,
          isCompleted: inboundCompleted,
          onOpen: latestInbound
            ? () => onOpenDocument?.('inbound', latestInbound.inbound_id)
            : undefined,
          onClick: hasInbound ? () => onNodeClick?.('inbound', flowDetail) : undefined,
        },
      },
      {
        id: 'inventory',
        type: 'stageNode',
        position: { x: 0, y: 0 },
        draggable: false,
        data: {
          stage: 'inventory',
          label: t('flow.stages.inventory', 'Inventory'),
          hasDocument: inboundCompleted,
          isCompleted: inboundCompleted,
          status: inboundCompleted ? 'completed' : undefined,
          onClick: inboundCompleted ? () => onNodeClick?.('inventory', flowDetail) : undefined,
        },
      },
      {
        id: 'invoice',
        type: 'stageNode',
        position: { x: 0, y: 0 },
        draggable: false,
        data: {
          stage: 'invoice',
          label: t('flow.stages.invoice', 'Invoice'),
          docNumber: latestInvoice?.invoice_number,
          status: latestInvoice?.status,
          amount: latestInvoice?.grand_total,
          currency: latestInvoice?.currency,
          date: latestInvoice?.due_date
            ? new Date(latestInvoice.due_date).toLocaleDateString()
            : undefined,
          hasDocument: hasInvoice,
          isCompleted: invoicePaid,
          isOverdue: latestInvoice?.status === 'overdue',
          onOpen: latestInvoice
            ? () => onOpenDocument?.('invoice', latestInvoice.invoice_id)
            : undefined,
          onClick: hasInvoice ? () => onNodeClick?.('invoice', flowDetail) : undefined,
        },
      },
    ];

    const edges: Edge[] = [
      {
        id: 'po-inbound',
        source: 'po',
        target: 'inbound',
        markerEnd: { type: MarkerType.ArrowClosed, width: 20, height: 20 },
        style: { strokeWidth: 2, stroke: hasInbound ? '#22c55e' : isOverduePO ? '#ef4444' : '#94a3b8' },
        animated: !hasInbound && !!po,
      },
      {
        id: 'inbound-inventory',
        source: 'inbound',
        target: 'inventory',
        markerEnd: { type: MarkerType.ArrowClosed, width: 20, height: 20 },
        style: { strokeWidth: 2, stroke: inboundCompleted ? '#22c55e' : hasInbound ? '#3b82f6' : '#94a3b8' },
        animated: hasInbound && !inboundCompleted,
      },
      {
        id: 'inventory-invoice',
        source: 'inventory',
        target: 'invoice',
        markerEnd: { type: MarkerType.ArrowClosed, width: 20, height: 20 },
        style: { strokeWidth: 2, stroke: invoicePaid ? '#22c55e' : inboundCompleted ? '#3b82f6' : '#94a3b8' },
        animated: inboundCompleted && !invoicePaid,
      },
    ];

    return getLayoutedElements(nodes as Node[], edges);
  }, [po, inbounds, invoices, t, onOpenDocument, onNodeClick, flowDetail, hasInbound, latestInbound, inboundCompleted, hasInvoice, latestInvoice, invoicePaid, isOverduePO]);

  const [nodes, , onNodesChange] = useNodesState(initialNodes);
  const [edges, , onEdgesChange] = useEdgesState(initialEdges);

  const minimapNodeColor = useCallback((node: Node) => {
    const data = node.data as unknown as StageNodeData;
    if (data.isOverdue) return '#ef4444';
    if (data.isCompleted) return '#22c55e';
    if (data.hasDocument) return '#3b82f6';
    return '#9ca3af';
  }, []);

  return (
    <div className="h-[400px] bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        nodeTypes={nodeTypes}
        fitView
        fitViewOptions={{ padding: 0.3 }}
        minZoom={0.5}
        maxZoom={1.5}
        nodesDraggable={false}
        nodesConnectable={false}
      >
        <Background color="#94a3b8" gap={20} size={1} />
        <Controls className="dark:bg-gray-700 dark:[&>button]:bg-gray-600 dark:[&>button]:text-gray-200 dark:[&>button]:border-gray-500" />
        <MiniMap nodeColor={minimapNodeColor} className="dark:bg-gray-700" />
      </ReactFlow>
      {/* Legend */}
      <div className="absolute bottom-4 left-4 flex gap-3 text-xs bg-white/90 dark:bg-gray-800/90 rounded px-3 py-2 shadow">
        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-green-500" /> Completed</span>
        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-blue-500" /> In Progress</span>
        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-red-500" /> Overdue</span>
        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-gray-400" /> Pending</span>
      </div>
    </div>
  );
};

export default ProcurementFlowView;
